import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation, useParams } from "react-router-dom";
import { captureLeadSource } from "@/lib/leadSource";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { PageViewTracker } from "@/components/PageViewTracker";
import { CartProvider } from "@/contexts/CartContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { Layout } from "@/components/layout/Layout";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Páginas públicas críticas: carregadas no bundle inicial
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

// Páginas públicas: lazy loaded
const Login          = lazy(() => import("./pages/Login"));
const Cadastro       = lazy(() => import("./pages/Cadastro"));
const Catalogo       = lazy(() => import("./pages/Catalogo"));
const Promocao       = lazy(() => import("./pages/Promocao"));
const Produto        = lazy(() => import("./pages/Produto"));
const Carrinho       = lazy(() => import("./pages/Carrinho"));
const CrieSuaMarca   = lazy(() => import("./pages/CrieSuaMarca"));
const MinhaConta     = lazy(() => import("./pages/MinhaConta"));
const EsqueciSenha   = lazy(() => import("./pages/EsqueciSenha"));
const ResetPassword  = lazy(() => import("./pages/ResetPassword"));
const Favoritos      = lazy(() => import("./pages/Favoritos"));
const PedidoLaboratorio   = lazy(() => import("./pages/PedidoLaboratorio"));
const LentesGrade    = lazy(() => import("./pages/LentesGrade"));
const Surfacado      = lazy(() => import("./pages/Surfacado"));
const SimuladorPublico    = lazy(() => import("./pages/SimuladorPublico"));
const TabelaPrecos   = lazy(() => import("./pages/TabelaPrecos"));
const ConfirmacaoOrcamento = lazy(() => import("./pages/ConfirmacaoOrcamento"));
const AutorMatheus   = lazy(() => import("./pages/AutorMatheus"));
const Autores        = lazy(() => import("./pages/Autores"));

/** Alias permanente /noticias/:slug -> /blog/:slug (URL canônica do artigo) */
function NoticiaRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/blog/${slug ?? ""}`} replace />;
}
const Comparar       = lazy(() => import("./pages/Comparar"));
const MeusPedidos    = lazy(() => import("./pages/MeusPedidos"));

// Blog: lazy loaded
const Blog           = lazy(() => import("./pages/Blog"));
const BlogPost       = lazy(() => import("./pages/BlogPost"));
const BlogCategoria  = lazy(() => import("./pages/BlogCategoria"));
const BlogTag        = lazy(() => import("./pages/BlogTag"));

// Lojista: lazy loaded
const Simulador           = lazy(() => import("./pages/Simulador"));
const LojistaDashboard    = lazy(() => import("./pages/LojistaDashboard"));
const LojistaConfiguracoes = lazy(() => import("./pages/LojistaConfiguracoes"));

// Admin: lazy loaded (pesadas, raramente visitadas)
const AdminDashboard      = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProdutos       = lazy(() => import("./pages/admin/AdminProdutos"));
const AdminOrcamentos     = lazy(() => import("./pages/admin/AdminOrcamentos"));
const AdminClientes       = lazy(() => import("./pages/admin/AdminClientes"));
const AdminImportar       = lazy(() => import("./pages/admin/AdminImportar"));
const AdminFotos          = lazy(() => import("./pages/admin/AdminFotos"));
const AdminBlog           = lazy(() => import("./pages/admin/AdminBlog"));
const AdminLojistas       = lazy(() => import("./pages/admin/AdminLojistas"));
const AdminConfiguracoes  = lazy(() => import("./pages/admin/AdminConfiguracoes"));
const AdminHome           = lazy(() => import("./pages/admin/AdminHome"));
const AdminLentes         = lazy(() => import("./pages/admin/AdminLentes"));
const AdminUsuarios       = lazy(() => import("./pages/admin/AdminUsuarios"));
const AdminPrivateLabel   = lazy(() => import("./pages/admin/AdminPrivateLabel"));
const AdminQuoteRequests  = lazy(() => import("./pages/admin/AdminQuoteRequests"));
const AdminAssets         = lazy(() => import("./pages/admin/AdminAssets"));
const AdminManual         = lazy(() => import("./pages/admin/AdminManual"));
const AdminAuditLog           = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminRLSAudit           = lazy(() => import("./pages/admin/AdminRLSAudit"));
const AdminFornecedores      = lazy(() => import("./pages/admin/AdminFornecedores"));
const AdminBling             = lazy(() => import("./pages/admin/AdminBling"));
const AdminFornecedorDetalhe = lazy(() => import("./pages/admin/AdminFornecedorDetalhe"));
const AdminLensCatalogCheck   = lazy(() => import("./pages/admin/AdminLensCatalogCheck"));
const AdminSetup              = lazy(() => import("./pages/admin/AdminSetup"));
const AdminSEO                = lazy(() => import("./pages/admin/AdminSEO"));
const AdminPaginaConsumidor   = lazy(() => import("./pages/admin/AdminPaginaConsumidor"));
const AdminPaginaB2B          = lazy(() => import("./pages/admin/AdminPaginaB2B"));
const AdminPromocoes          = lazy(() => import("./pages/admin/AdminPromocoes"));

const ParaVoce                = lazy(() => import("./pages/ParaVoce"));
const ParaSuaOtica            = lazy(() => import("./pages/ParaSuaOtica"));
const SolicitarAcessoAdmin    = lazy(() => import("./pages/SolicitarAcessoAdmin"));
const CatalogoLentesLojista   = lazy(() => import("./pages/CatalogoLentesLojista"));
const PoliticaDevolucao       = lazy(() => import("./pages/PoliticaDevolucao"));
const SeoLanding              = lazy(() => import("./pages/SeoLanding"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 2 * 60 * 1000,
    },
  },
});

function PageFallback() {
  return <div className="min-h-[60vh] bg-background animate-pulse" aria-hidden="true" />;
}

// Captura utm, gclid e publico da URL (anúncios) e guarda na sessão p/ o link do WhatsApp.
function LeadSourceCapture() {
  const location = useLocation();
  useEffect(() => {
    captureLeadSource();
  }, [location.pathname, location.search]);
  return null;
}

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <PageViewTracker />
        <LeadSourceCapture />
        <AuthProvider>
          <CartProvider>
          <FavoritesProvider>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* Admin routes - no Layout wrapper */}
                <Route path="/admin"                  element={<AdminDashboard />} />
                <Route path="/admin/produtos"         element={<AdminProdutos />} />
                <Route path="/admin/orcamentos"       element={<AdminOrcamentos />} />
                <Route path="/admin/clientes"         element={<AdminClientes />} />
                <Route path="/admin/fotos"            element={<AdminFotos />} />
                <Route path="/admin/importar"         element={<AdminImportar />} />
                <Route path="/admin/blog"             element={<AdminBlog />} />
                <Route path="/admin/lojistas"         element={<AdminLojistas />} />
                <Route path="/admin/configuracoes"    element={<AdminConfiguracoes />} />
                <Route path="/admin/home"             element={<AdminHome />} />
                <Route path="/admin/lentes"           element={<AdminLentes />} />
                <Route path="/admin/usuarios"         element={<AdminUsuarios />} />
                <Route path="/admin/private-label"    element={<AdminPrivateLabel />} />
                <Route path="/admin/solicitacoes"     element={<AdminQuoteRequests />} />
                <Route path="/admin/assets"           element={<AdminAssets />} />
                <Route path="/admin/manual"           element={<AdminManual />} />
                <Route path="/admin/audit-log"        element={<AdminAuditLog />} />
                <Route path="/admin/rls-audit"        element={<AdminRLSAudit />} />
                <Route path="/admin/fornecedores"      element={<AdminFornecedores />} />
                <Route path="/admin/bling"             element={<AdminBling />} />
                <Route path="/admin/fornecedores/:id"  element={<AdminFornecedorDetalhe />} />
                <Route path="/admin/lens-seed-check"  element={<AdminLensCatalogCheck />} />
                <Route path="/admin/setup"            element={<AdminSetup />} />
                <Route path="/admin/seo"              element={<AdminSEO />} />
                <Route path="/admin/pagina-consumidor" element={<AdminPaginaConsumidor />} />
                <Route path="/admin/pagina-b2b"        element={<AdminPaginaB2B />} />
                <Route path="/admin/promocoes"         element={<AdminPromocoes />} />

                {/* Retailer routes - no Layout wrapper */}
                <Route path="/simulador"              element={<Simulador />} />
                <Route path="/lojista"                element={<LojistaDashboard />} />
                <Route path="/lojista/configuracoes"  element={<LojistaConfiguracoes />} />
                <Route path="/lojista/lentes"         element={<CatalogoLentesLojista />} />
                {/* Public routes */}
                <Route path="/"                       element={<Layout><Home /></Layout>} />
                <Route path="/login"                  element={<Layout><Login /></Layout>} />
                <Route path="/cadastro"               element={<Layout><Cadastro /></Layout>} />
                <Route path="/catalogo"               element={<Layout><Catalogo /></Layout>} />
                <Route path="/promocao"               element={<Layout><Promocao /></Layout>} />
                <Route path="/promocoes"              element={<Layout><Promocao /></Layout>} />
                <Route path="/oculos-solares"                     element={<Layout><Catalogo /></Layout>} />

                <Route path="/oculos-solares/:genero"             element={<Layout><Catalogo /></Layout>} />
                <Route path="/oculos-solares/:genero/:formato"    element={<Layout><Catalogo /></Layout>} />
                <Route path="/armacoes-de-grau"                   element={<Layout><Catalogo /></Layout>} />
                <Route path="/armacoes-de-grau/:genero"           element={<Layout><Catalogo /></Layout>} />
                <Route path="/armacoes-de-grau/:genero/:material" element={<Layout><Catalogo /></Layout>} />
                <Route path="/oculos-esportivos"                  element={<Layout><Catalogo /></Layout>} />
                <Route path="/oculos-esportivos/:atributo"        element={<Layout><Catalogo /></Layout>} />
                {/* Landing pages SEO fundo de funil (conteúdo em src/data/seoLandings.json) */}
                <Route path="/fornecedor-para-oticas"  element={<Layout><SeoLanding slug="fornecedor-para-oticas" /></Layout>} />
                <Route path="/atacado-de-oculos"       element={<Layout><SeoLanding slug="atacado-de-oculos" /></Layout>} />
                <Route path="/oculos-para-revenda"     element={<Layout><SeoLanding slug="oculos-para-revenda" /></Layout>} />
                <Route path="/fornecedor-de-lentes"    element={<Layout><SeoLanding slug="fornecedor-de-lentes" /></Layout>} />
                <Route path="/acessorios-para-oticas"  element={<Layout><SeoLanding slug="acessorios-para-oticas" /></Layout>} />
                <Route path="/produto/:id"            element={<Layout><Produto /></Layout>} />
                <Route path="/carrinho"               element={<Layout><Carrinho /></Layout>} />
                <Route path="/pedido-confirmado"      element={<Layout><ConfirmacaoOrcamento /></Layout>} />
                <Route path="/crie-sua-marca"         element={<Layout><CrieSuaMarca /></Layout>} />
                <Route path="/lentes"                 element={<Navigate to="/lentes-grade" replace />} />
                <Route path="/simulador-publico"      element={<Layout><SimuladorPublico /></Layout>} />
                <Route path="/esqueci-senha"          element={<Layout><EsqueciSenha /></Layout>} />
                <Route path="/solicitar-acesso-admin" element={<Layout><SolicitarAcessoAdmin /></Layout>} />
                <Route path="/reset-password"         element={<Layout><ResetPassword /></Layout>} />
                <Route path="/minha-conta"            element={<Layout><MinhaConta /></Layout>} />
                <Route path="/meus-pedidos"           element={<Layout><MeusPedidos /></Layout>} />
                <Route path="/pedido-laboratorio"     element={<Layout><PedidoLaboratorio /></Layout>} />
                <Route path="/lentes-grade"           element={<Layout><LentesGrade /></Layout>} />
                <Route path="/surfacado"              element={<Layout><Surfacado /></Layout>} />
                <Route path="/blog"                   element={<Layout><Blog /></Layout>} />
                <Route path="/blog/categoria/:slug"   element={<Layout><BlogCategoria /></Layout>} />
                <Route path="/blog/tag/:tag"          element={<Layout><BlogTag /></Layout>} />
                <Route path="/blog/:slug"             element={<Layout><BlogPost /></Layout>} />
                <Route path="/autor/magno-carraro"    element={<Layout><AutorMatheus /></Layout>} />
                <Route path="/autor/matheus-silva"    element={<Navigate to="/autor/magno-carraro" replace />} />
                <Route path="/autores"                element={<Layout><Autores /></Layout>} />
                <Route path="/noticias"               element={<Navigate to="/blog" replace />} />
                <Route path="/noticias/:slug"         element={<NoticiaRedirect />} />
                <Route path="/favoritos"              element={<Layout><Favoritos /></Layout>} />
                <Route path="/tabela-precos"          element={<Layout><TabelaPrecos /></Layout>} />
                <Route path="/comparar"               element={<Layout><Comparar /></Layout>} />
                <Route path="/para-voce"              element={<Layout><ParaVoce /></Layout>} />
                <Route path="/para-sua-otica"         element={<Layout><ParaSuaOtica /></Layout>} />
                <Route path="/politica-de-devolucao"  element={<Layout><PoliticaDevolucao /></Layout>} />
                <Route path="/politica-de-trocas"     element={<Navigate to="/politica-de-devolucao" replace />} />
                <Route path="*"                       element={<Layout><NotFound /></Layout>} />
              </Routes>
            </Suspense>
          </FavoritesProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
